import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { plainToInstance } from "class-transformer";
import { useEffect } from "react";
import { ApiRes, ErrorRes, PlainRes, SuccessRes } from "@/dto/HttpDto.ts";
import { EndpointConfig, EndpointEnum, QueryKeyType } from "@/enum/EndpointEnum.ts";
import HttpUtil, { HttpConfigInterface } from "@/util/HttpUtil.ts";
import { showLoading } from "react-global-loading";

export interface GetQueryProps<TRequest extends PlainRes = PlainRes, TResponse extends SuccessRes = SuccessRes> extends Omit<HttpConfigInterface<TRequest>, "endpointEnum"> {
    options?: Omit<UseQueryOptions<PlainRes, ErrorRes, ApiRes<TResponse>, QueryKeyType>, "queryKey" | "queryFn">;
    onSuccess?: (res: TResponse) => void;
    onError?: (res: ErrorRes) => void;
}

const useGetQuery = <TRequest extends PlainRes = PlainRes, TResponse extends SuccessRes = SuccessRes>(endpointEnum: EndpointEnum, queryOption?: GetQueryProps<TRequest, TResponse>): UseQueryResult<ApiRes<TResponse>, ErrorRes> => {
    const config = EndpointConfig[endpointEnum];
    const queryClient = useQueryClient();

    const getQueryKey = (req: TRequest): QueryKeyType => {
        return config?.queryKey ? [endpointEnum, ...config.queryKey(req)] : [endpointEnum];
    };

    const { refetch, ...result } = useQuery<PlainRes, ErrorRes, ApiRes<TResponse>, QueryKeyType>(
        {
            queryKey: getQueryKey(queryOption?.params as TRequest),
            queryFn: () => {
                if (config.showLoading) {
                    showLoading(true);
                }
                const requestDto = plainToInstance<TRequest, PlainRes>(config.requestDto, queryOption?.params || {});
                const result = HttpUtil.axiosRequest<TRequest, SuccessRes>({
                    endpointEnum,
                    params: requestDto,
                    headers: queryOption?.headers,
                    responseDto: config.responseDto,
                });
                if (config.showLoading) {
                    showLoading(false);
                }
                return result;
            },
            ...queryOption?.options,
        },
        queryClient,
    );

    const handleResult = (data: ApiRes<TResponse>) => {
        if ("error" in data) {
            queryOption?.onError?.(data);
            return;
        }
        queryOption?.onSuccess?.(data);
    };

    useEffect(() => {
        if (!result.data) {
            return;
        }
        handleResult(result.data);
    }, [result.data]);

    return { ...result, refetch };
};
export default useGetQuery;